import React, {useState, useEffect} from 'react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, Brush, ReferenceLine, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Label } from 'recharts';
import TimeRangeContainer from '../utils/TimeRangeContainer';
import Loading from '../Loading';
import AxisSettingButton from '../utils/AxisSettingButton';
import NormalizeSettingButton from '../utils/NormalizeSettingButton';
import { japaneseTickFormatter } from '../utils/commonUtils';
import NotebookIcon from '../NotebookIcon';
import { formatDateTimeString } from '../utils/commonUtils';
import { convertSingleValue } from '../utils/commonUtils';

const AnalysisMarketLineGraph = ({ data, companies, handleBookmarkClick, chatHistoryId, showSaveNotebookIcon=false, height = 500 }) => {
    const [selectedRange, setSelectedRange] = useState('5Y');
    const [modifiedData, setModifiedData] = useState(null);
    const [filteredData, setfilteredData] = useState(null);
    const [labelState, setLabelState] = useState('auto');
    const [normalizeState, setNormalizeState] = useState(false);
    const [showLoading, setShowLoading] = useState(false);
    
    const modifyData = (data) => {
        return data.map((item) => {
          const newItem = { ...item };
          Object.keys(newItem).forEach((key) => {
            if (key !== 'date' && newItem[key] === 0) {
              newItem[key] = null; // 0 を null に変換
            }
          });
          return newItem;
        });
      };

    useEffect(() => {
        const adjustedData = adjustTimeRange(data);
        setModifiedData(adjustedData);
    }, [selectedRange])


    useEffect(() => {
        if (modifiedData) {
            if (normalizeState) {
                const normalizedData = normalizeData(modifiedData.data)
                setfilteredData(modifyData(normalizedData))   
            } else {
                setfilteredData(modifiedData.data)
            }
        }
    }, [modifiedData, normalizeState])

    const normalizeData = (data) => {
        const baseValues = { ...data[0] };
        delete baseValues.date;

        // データを基準化
        const normalizedData = data.map(entry => {
        const newEntry = { date: entry.date }; // dateはそのまま保持
        for (const key in entry) {
            if (key === 'date') continue; // dateは無視

            // 基準値が存在しない場合はそのまま返す
            if (baseValues[key] === undefined || baseValues[key] === null) {
            newEntry[key] = entry[key];
            } else {
            // 基準値が存在する場合は正規化
            newEntry[key] = entry[key] / baseValues[key];
            }
        }
        return newEntry;
        });

        return normalizedData
    }

    const MAX_NUM_OF_DATA = 5;
    const stopColor = [
        { start: 'rgba(145, 186, 250, 1)', end: 'rgba(80, 140, 200, 1)' }, // 青
        { start: 'rgba(90, 195, 90, 1)', end: 'rgba(50, 110, 50, 1)' }, // 緑
        { start: 'rgba(225, 97, 50, 1)', end: 'rgba(255, 120, 90, 1)' }, // オレンジ
        { start: 'rgba(147, 112, 219, 1)', end: 'rgba(75, 0, 130, 1)' },  // 紫
        { start: 'rgba(255, 235, 59, 1)', end: 'rgba(251, 192, 45, 1)' }, // 黄色
    ];

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip px-3 py-2 border border-gray-400 bg-gray-600 rounded shadow text-white">
                    <div>{`${formatDateTimeString(payload[0].payload.date, "YYYY年m月d日")}`}</div>
                    {payload.map((item, index) => {
                        const seriesIndex = companies.findIndex(company => company.secCode === item.dataKey);
                        const labelColor = stopColor[seriesIndex]?.start || 'rgba(100, 100, 100, 0.7)';
                        const unit = normalizeState ? 
                            '' : 
                            (data.unit === 'value') ? data.currency[index] : data.unit;
                        return (
                            <div key={index} style={{ display: 'flex', alignItems: 'center'}}>
                                <div
                                    style={{
                                        width: '10px',
                                        height: '10px',
                                        borderRadius: '50%',
                                        backgroundColor: labelColor,
                                        marginRight: '8px'
                                    }}
                                ></div>
                                {/* テキスト部分 */}
                                <div className="text-white m-1">
                                    {`${item.name}: ${(convertSingleValue(item.payload[item['dataKey']]))}${unit}`}
                                </div>
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };
    
    const timeRanges = [
        // ...( ['stock_price', 'volume'].includes(data.col) ? [{ label: '1D', value: '1D' }] : [] ),
        { label: '5D', value: '5D' },
        { label: '1M', value: '1M' },
        { label: '6M', value: '6M' },
        { label: '1Y', value: '1Y' },
        { label: '3Y', value: '3Y' },
        { label: '5Y', value: '5Y' },
        { label: '10Y', value: '10Y' },
        { label: '20Y', value: '20Y' },
        { label: 'MAX', value: 'MAX' }
    ];

    const handleTimeRangeSetting = (newRange) => {
        setSelectedRange(newRange);
    }
    console.log(data)

    const getTickData = async (secCodes) => {
        // setShowLoading(true)
        // try {
        //     const { dateFrom, dateTo } = getTickDateRange();
        //     const response = await fetchTickData(secCode, dateFrom, dateTo);
        //     const fetchedData = response.data // データを逆順にする
        //     if (fetchedData.length === 0) return [];
    
        //     // 最初の要素の日付（YYYY-MM-DD形式に変換）
        //     const firstDate = fetchedData[0].date.split(' ')[0];
        //     // 同じ日付のデータだけをフィルタリング
        //     const filteredData = fetchedData.reverse().filter(item => item.date.startsWith(firstDate));
        //     setShowLoading(false)
        //     return filteredData;
        // } catch (error) {
        //     console.error('Error in getTickData:', error);
        //     setShowLoading(false)
        //     return [];
        // }
    };
    
    const adjustTimeRange = (data) => {
        if (!data || data.data.length === 0) return 0;
        if (data.length < 2) return data;
        let displayData;
        displayData = data.data;
        const latestDate = new Date(displayData[displayData.length - 1].date);
        let startDate = new Date(latestDate);
        switch (selectedRange) {
            case '1D':
                displayData = getTickData(data.companies.map((c) => c.secCode))
            case '5D':
                displayData = displayData.slice(displayData.length - 4, displayData.length)
                return {...data, data: displayData}
            case '5D':
                displayData = displayData.slice(displayData.length - 4, displayData.length)
                return {...data, data: displayData}
            case '1M':
                startDate.setMonth(latestDate.getMonth() - 1);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                return {...data, data: displayData}
            case '6M':
                startDate.setMonth(latestDate.getMonth() - 6);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                return {...data, data: displayData}
            case '1Y':
                startDate.setFullYear(latestDate.getFullYear() - 1);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                return {...data, data: displayData}
            case '3Y':
                startDate.setFullYear(latestDate.getFullYear() - 3);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                return {...data, data: displayData}
            case '5Y':
                startDate.setFullYear(latestDate.getFullYear() - 5);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                return {...data, data: displayData}
            case '10Y':
                    startDate.setFullYear(latestDate.getFullYear() - 10);
                    displayData = displayData.filter(item => new Date(item.date) >= startDate);
                    return {...data, data: displayData}
            case '20Y':
                startDate.setFullYear(latestDate.getFullYear() - 20);
                displayData = displayData.filter(item => new Date(item.date) >= startDate);
                return {...data, data: displayData}
            case 'MAX':
                return data
            default: 
                return data

        }
    }

    const handleLabelSettingClick = (newState) => {
        setLabelState(newState);
    }

    const handleNormalizeSettingClick = (newState) => {
        setNormalizeState(newState);
    }

    const extractAllValues = (data) => {
        return data.reduce((acc, item) => {
            Object.keys(item).forEach(key => {
                if (key !== "date") {
                    acc.push(item[key]);
                }
            });
            return acc;
        }, []);
    };

    const renderCustomLegend = () => (
        <div className="flex flex-wrap pb-[20px] gap-4">
            {companies.map((series, index) => (
                <div 
                    key={index} 
                    className="flex items-center" 
                    style={{ color: stopColor[index % stopColor.length].start }}
                >
                    <div className="mr-2 flex items-center">
                        <svg width="10" height="10">
                            <line x1="0" y1="5" x2="10" y2="5" stroke={stopColor[index % stopColor.length].start} strokeWidth="2" />
                        </svg>
                    </div>
                    <div className="text-gray-600">{series.companyName}</div>
                </div>
            ))}
        </div>
    );
    

    if (data.data.length === 0) {
        return <div>株価データなし</div>
    }

    if (!filteredData) {
        return <Loading />
    }

    if (companies.length === 0) {
        return <Loading />
    }

    return (
        <>
            <div className="analysis-menu-bar flex flex-wrap mb-4 gap-2 items-center" >
                <div className='graph-title ml-2 text-xl bold text-gray-600'>{data.name}</div>
                {showSaveNotebookIcon && <div className='mb-4'><NotebookIcon chatHistoryId={chatHistoryId} onClick={handleBookmarkClick} /></div>}
                <TimeRangeContainer timeRanges={timeRanges} handleTimeRangeSetting={handleTimeRangeSetting} selectedRange={selectedRange}/>
                <AxisSettingButton handleLabelSettingClick={handleLabelSettingClick} labelState={labelState} /> 
                <NormalizeSettingButton handleNormalizeSettingClick={handleNormalizeSettingClick} normalizeState={normalizeState} /> 
            </div>
            <ResponsiveContainer width="100%" height={height}>
                <AreaChart
                data={filteredData}
                margin={{ top: 20, right: 5, left: 5, bottom: 40 }}
                >
                <defs>
                    {stopColor.map((color, index) => (
                    <linearGradient
                        id={`gradient${index}`}
                        key={index}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                    >
                        <stop offset="5%" stopColor={color.start} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={color.end} stopOpacity={0.2} />
                    </linearGradient>
                    ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
                <ReferenceLine y={0} stroke="lightgray" />
                <YAxis
                    orientation="right"
                    tickFormatter={(value) => japaneseTickFormatter(value, '')}
                    tickCount={4}
                >
                    <Label
                    position="top"
                    offset={20}
                    style={{ textAnchor: 'middle', fill: 'grey' }}
                    />
                </YAxis>
                <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#000' }} />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: 'rgba(200, 200, 200, 0.5)' }}
                />
                <Legend content={renderCustomLegend} verticalAlign="top" align="left" />
                {companies.slice(0, MAX_NUM_OF_DATA).map((company, seriesIndex) => (
                    <Area
                    key={company.secCode}
                    dataKey={company.secCode}
                    type="linear"
                    stroke={stopColor[seriesIndex]['start']}
                    fill={`url(#gradient${seriesIndex})`}
                    strokeWidth={2}
                    name={company.companyName}
                    />
                ))}
                <Brush dataKey="date" height={15} stroke="lightgrey" />
                </AreaChart>
            </ResponsiveContainer>
        </>
    );
};

export default AnalysisMarketLineGraph;
